import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { motion } from "framer-motion";
import { Copy, RotateCcw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { Button } from "@/components/ui/button";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  addPercent,
  percentChange,
  percentOfNumber,
  reverseOf,
  subtractPercent,
  whatPercent,
} from "@/lib/calculators/percentage";

type B = { en: string; bn: string };
type Mode = "of" | "isOf" | "change" | "addsub" | "reverse";

const MODES: { value: Mode; label: B }[] = [
  { value: "of", label: { en: "X% of Y", bn: "Y এর X%" } },
  { value: "isOf", label: { en: "X is what % of Y", bn: "X = Y এর কত %" } },
  { value: "change", label: { en: "Increase / Decrease", bn: "বৃদ্ধি / হ্রাস" } },
  { value: "addsub", label: { en: "Add / Subtract %", bn: "% যোগ / বিয়োগ" } },
  { value: "reverse", label: { en: "Reverse: X% of what is Y", bn: "রিভার্স: X% এর কত = Y" } },
];

const T = {
  h1: { en: "Percentage Calculator", bn: "পার্সেন্টেজ ক্যালকুলেটর" },
  desc: {
    en: "Multi-mode percentage calculator: percent of a number, what percent, increase/decrease, add/subtract, and reverse percentage.",
    bn: "একাধিক মোডে পার্সেন্টেজ হিসাব: কোনো সংখ্যার শতকরা, শতাংশ বের করা, বৃদ্ধি/হ্রাস, যোগ/বিয়োগ, রিভার্স।",
  },
  result: { en: "Result", bn: "ফলাফল" },
  formula: { en: "Formula", bn: "সূত্র" },
  reset: { en: "Reset", bn: "রিসেট" },
  copy: { en: "Copy", bn: "কপি" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },

  pct: { en: "Percentage (%)", bn: "শতকরা (%)" },
  number: { en: "Number", bn: "সংখ্যা" },
  part: { en: "Part (X)", bn: "অংশ (X)" },
  whole: { en: "Whole (Y)", bn: "মোট (Y)" },
  from: { en: "From (original)", bn: "প্রাথমিক মান" },
  to: { en: "To (new)", bn: "নতুন মান" },
  partial: { en: "Partial value (Y)", bn: "আংশিক মান (Y)" },

  add: { en: "Add", bn: "যোগ" },
  subtract: { en: "Subtract", bn: "বিয়োগ" },
  op: { en: "Operation", bn: "অপারেশন" },

  increase: { en: "increase", bn: "বৃদ্ধি" },
  decrease: { en: "decrease", bn: "হ্রাস" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "How to find a percentage of a number?", bn: "কোনো সংখ্যার শতকরা কীভাবে বের করব?" },
    a: {
      en: "Multiply the number by the percentage and divide by 100. Example: 30% of 200 = (30 × 200) / 100 = 60. Use the 'X% of Y' tab above to do this instantly.",
      bn: "সংখ্যাকে শতকরা দিয়ে গুণ করে ১০০ দিয়ে ভাগ করুন। উদাহরণ: ২০০ এর ৩০% = (৩০ × ২০০) / ১০০ = ৬০। উপরে 'Y এর X%' ট্যাব ব্যবহার করুন।",
    },
  },
  {
    q: { en: "What is the percentage increase formula?", bn: "শতকরা বৃদ্ধির সূত্র কী?" },
    a: {
      en: "% Change = ((New − Old) / Old) × 100. A positive result is increase, negative is decrease. Example: from 100 to 130 → ((130−100)/100)×100 = +30% increase.",
      bn: "% পরিবর্তন = ((নতুন − পুরাতন) / পুরাতন) × ১০০। ধনাত্মক মানে বৃদ্ধি, ঋণাত্মক মানে হ্রাস। উদাহরণ: ১০০ → ১৩০ মানে +৩০% বৃদ্ধি।",
    },
  },
  {
    q: { en: "পার্সেন্টেজ কীভাবে গণনা করা হয়?", bn: "পার্সেন্টেজ কীভাবে গণনা করা হয়?" },
    a: {
      en: "শতকরা = (অংশ / মোট) × ১০০। উদাহরণ: ৬০ মার্ক ২০০ এর কত % = (৬০/২০০) × ১০০ = ৩০%।",
      bn: "শতকরা = (অংশ / মোট) × ১০০। উদাহরণ: ৬০ মার্ক ২০০ এর কত % = (৬০/২০০) × ১০০ = ৩০%।",
    },
  },
  {
    q: { en: "How do I add 15% to a price?", bn: "কোনো মূল্যে ১৫% যোগ করব কীভাবে?" },
    a: {
      en: "New = Original + (Original × % / 100), or simply Original × 1.15 for 15%. Example: ৳200 + 15% = 200 × 1.15 = ৳230. Use the 'Add / Subtract %' tab.",
      bn: "নতুন = মূল + (মূল × % / ১০০), অথবা ১৫% এর জন্য মূল × ১.১৫। উদাহরণ: ৳২০০ + ১৫% = ২০০ × ১.১৫ = ৳২৩০। 'যোগ/বিয়োগ' ট্যাব ব্যবহার করুন।",
    },
  },
  {
    q: { en: "How is reverse percentage calculated?", bn: "রিভার্স পার্সেন্টেজ কীভাবে?" },
    a: {
      en: "If X% of some total equals Y, then total = Y ÷ (X/100). Example: 30% of what is 60 → 60 ÷ 0.30 = 200. Useful for backing out the original price from a sale price.",
      bn: "X% যদি Y এর সমান হয়, তাহলে মোট = Y ÷ (X/১০০)। উদাহরণ: ৩০% = ৬০ হলে মোট = ৬০ ÷ ০.৩০ = ২০০। সেল মূল্য থেকে আসল মূল্য বের করতে কাজে লাগে।",
    },
  },
  {
    q: { en: "Where are percentages used in daily life?", bn: "দৈনন্দিন জীবনে % কোথায় ব্যবহৃত হয়?" },
    a: {
      en: "Discounts, VAT/sales tax, exam marks (SSC/HSC GPA), bank interest, salary hike, profit margin, body fat %, election results, and many more — almost every quantitative comparison.",
      bn: "ডিসকাউন্ট, VAT, পরীক্ষার নম্বর (SSC/HSC GPA), ব্যাংকের সুদ, বেতন বৃদ্ধি, লাভের হার, শরীরের চর্বি %, নির্বাচনের ফল — প্রায় প্রতিটি পরিমাণগত তুলনায়।",
    },
  },
];

const searchSchema = z.object({
  mode: fallback(z.enum(["of", "isOf", "change", "addsub", "reverse"]).optional(), "of").default("of"),
});

export const Route = createFileRoute("/percentage-calculator")({
  validateSearch: zodValidator(searchSchema),
  head: () => {
    const seo = buildSeo({
      title: "Percentage Calculator — % Of, Increase, Decrease",
      description:
        "Free percentage calculator: what % of, increase/decrease, add/subtract percentage. Multiple modes for any calculation. পার্সেন্টেজ ক্যালকুলেটর।",
      path: "/percentage-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Percentage Calculator", path: "/percentage-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: PercentageCalculatorPage,
});

function PercentageCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);

  const { mode = "of" } = Route.useSearch();
  const navigate = useNavigate({ from: "/percentage-calculator" });
  const setMode = (m: Mode) => navigate({ search: { mode: m }, replace: true });

  // shared inputs (different fields used per mode)
  const [a, setA] = useState("30");
  const [b, setB] = useState("200");
  const [op, setOp] = useState<"add" | "sub">("add");

  // Sensible defaults per mode without resetting on every switch
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number) => num(roundSmart(n));

  const x = parseFloat(a) || 0;
  const y = parseFloat(b) || 0;

  const { value, sentence, formula } = useMemo(() => {
    switch (mode) {
      case "of": {
        const v = percentOfNumber(x, y);
        return {
          value: v,
          sentence: lang === "en"
            ? `${num(x)}% of ${num(y)} is ${fmt(v)}`
            : `${num(y)} এর ${num(x)}% হলো ${fmt(v)}`,
          formula: "(X / 100) × Y",
        };
      }
      case "isOf": {
        const v = whatPercent(x, y);
        return {
          value: v,
          sentence: lang === "en"
            ? `${num(x)} is ${fmt(v)}% of ${num(y)}`
            : `${num(x)} হলো ${num(y)} এর ${fmt(v)}%`,
          formula: "(X / Y) × 100",
        };
      }
      case "change": {
        const v = percentChange(x, y);
        const dir = v >= 0 ? T.increase[lang] : T.decrease[lang];
        return {
          value: v,
          sentence: lang === "en"
            ? `From ${num(x)} to ${num(y)} is a ${fmt(Math.abs(v))}% ${dir}`
            : `${num(x)} থেকে ${num(y)} হলো ${fmt(Math.abs(v))}% ${dir}`,
          formula: "((To − From) / From) × 100",
        };
      }
      case "addsub": {
        const v = op === "add" ? addPercent(y, x) : subtractPercent(y, x);
        return {
          value: v,
          sentence: lang === "en"
            ? `${num(y)} ${op === "add" ? "+" : "−"} ${num(x)}% = ${fmt(v)}`
            : `${num(y)} ${op === "add" ? "+" : "−"} ${num(x)}% = ${fmt(v)}`,
          formula: op === "add" ? "Y + (Y × X / 100)" : "Y − (Y × X / 100)",
        };
      }
      case "reverse":
      default: {
        const v = reverseOf(x, y);
        return {
          value: v,
          sentence: lang === "en"
            ? `${num(x)}% of ${fmt(v)} is ${num(y)}`
            : `${fmt(v)} এর ${num(x)}% হলো ${num(y)}`,
          formula: "Y / (X / 100)",
        };
      }
    }
  }, [mode, x, y, op, lang, useBn]);

  const reset = () => {
    setA("30");
    setB("200");
    setOp("add");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sentence);
    } catch { /* noop */ }
  };

  // Field labels per mode
  const aLabel = mode === "isOf" ? T.part[lang]
    : mode === "change" ? T.from[lang]
    : mode === "addsub" ? T.pct[lang]
    : T.pct[lang];
  const bLabel = mode === "isOf" ? T.whole[lang]
    : mode === "change" ? T.to[lang]
    : mode === "addsub" ? T.number[lang]
    : mode === "reverse" ? T.partial[lang]
    : T.number[lang];
  const aUnit = (mode === "isOf" || mode === "change") ? "" : "%";
  const bUnit = (mode === "isOf" || mode === "change") ? "" : "";

  return (
    <CalculatorLayout
      slug="percentage-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Percent"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Mode tabs (URL-synced via ?mode=) */}
      <div className="flex flex-wrap gap-1.5">
        {MODES.map((m) => {
          const active = mode === m.value;
          return (
            <button
              key={m.value}
              type="button"
              onClick={() => setMode(m.value)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                active
                  ? "border-accent bg-accent text-accent-foreground"
                  : "border-border bg-card text-foreground/80 hover:border-accent/40",
                lang === "bn" && "font-bn",
              )}
            >
              {m.label[lang]}
            </button>
          );
        })}
      </div>

      {/* Inputs */}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <NumberInput label={aLabel} value={a} onChange={setA} unit={aUnit} />
        <NumberInput label={bLabel} value={b} onChange={setB} unit={bUnit} />
      </div>

      {mode === "addsub" && (
        <div className="mt-4 space-y-2">
          <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("op")}</p>
          <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
            {(["add", "sub"] as const).map((o) => {
              const active = op === o;
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOp(o)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition",
                    active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {o === "add" ? tr("add") : tr("subtract")}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-3">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input
            type="checkbox"
            checked={bnDigits}
            onChange={(e) => setBnDigits(e.target.checked)}
            className="h-3.5 w-3.5 accent-accent"
          />
          {tr("bnDigits")}
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={copy} className="gap-2"><Copy size={14} /> {tr("copy")}</Button>
          <Button variant="outline" onClick={reset} className="gap-2"><RotateCcw size={14} /> {tr("reset")}</Button>
        </div>
      </div>

      {/* Result */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-8 space-y-4"
      >
        <div className="rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center">
          <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>
            {tr("result")}
          </p>
          <p className="mt-2 text-4xl font-semibold tabular-nums md:text-5xl">
            {fmt(value)}
            {mode === "isOf" || mode === "change" ? "%" : ""}
          </p>
          <p className={cn("mt-3 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{sentence}</p>
        </div>
        <p className={cn("text-center text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          {tr("formula")}: <code className="rounded bg-muted px-1.5 py-0.5 font-mono">{formula}</code>
        </p>
      </motion.div>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "Basic percentage formula" : "মৌলিক শতকরা সূত্র"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <strong>X% of Y = (X / 100) × Y</strong> &nbsp;|&nbsp; <strong>X is what % of Y = (X / Y) × 100</strong>
            <br />
            {lang === "en"
              ? "Example: 30% of 200 = (30/100)×200 = 60. Conversely, 60 is (60/200)×100 = 30% of 200."
              : "উদাহরণ: ২০০ এর ৩০% = (৩০/১০০)×২০০ = ৬০। আবার ৬০ হলো (৬০/২০০)×১০০ = ২০০ এর ৩০%।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Percentage increase / decrease" : "শতকরা বৃদ্ধি / হ্রাস"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <strong>% Change = ((New − Old) / Old) × 100</strong>
            <br />
            {lang === "en"
              ? "From 100 to 130 → +30% increase. From 100 to 75 → −25% decrease. Note: a 50% drop followed by a 50% rise does NOT return to the original — it returns to 75% of original."
              : "১০০ → ১৩০ মানে +৩০% বৃদ্ধি। ১০০ → ৭৫ মানে −২৫% হ্রাস। ৫০% হ্রাসের পর ৫০% বৃদ্ধি মূল মানে ফেরে না — মূলের ৭৫% তে যায়।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Real-life uses" : "দৈনন্দিন ব্যবহার"} lang={lang}>
          <ul className={cn("list-disc space-y-1.5 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Discounts during Eid / Pohela Boishakh sales." : "ঈদ / পহেলা বৈশাখ সেলে ডিসকাউন্ট।"}</li>
            <li>{lang === "en" ? "VAT 15% added to a sale price." : "বিক্রয়মূল্যে ১৫% VAT যোগ।"}</li>
            <li>{lang === "en" ? "SSC / HSC marks → percentage and GPA." : "SSC / HSC নম্বর → শতকরা ও GPA।"}</li>
            <li>{lang === "en" ? "Salary increment, bank interest rate, profit margin." : "বেতন বৃদ্ধি, ব্যাংকের সুদ হার, লাভের হার।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "SSC / HSC math examples" : "SSC / HSC গণিত উদাহরণ"} lang={lang}>
          <ol className={cn("list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "A student scored 384 out of 500. Percentage = (384/500)×100 = 76.8%." : "একজন ছাত্র ৫০০ এর মধ্যে ৩৮৪ পেল। শতকরা = (৩৮৪/৫০০)×১০০ = ৭৬.৮%।"}</li>
            <li>{lang === "en" ? "Population grew from 1,60,000 to 1,76,000 in 5 years → +10% growth." : "জনসংখ্যা ৫ বছরে ১,৬০,০০০ থেকে ১,৭৬,০০০ হলো → +১০% বৃদ্ধি।"}</li>
            <li>{lang === "en" ? "Cost price ৳120, selling price ৳150 → profit % = (30/120)×100 = 25%." : "ক্রয়মূল্য ৳১২০, বিক্রয়মূল্য ৳১৫০ → লাভ % = (৩০/১২০)×১০০ = ২৫%।"}</li>
          </ol>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/discount-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Discount" : "ডিসকাউন্ট"}</Link>
            <Link to="/vat-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">VAT</Link>
            <Link to="/tax-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Income Tax" : "আয়কর"}</Link>
            <Link to="/gpa-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">GPA</Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function roundSmart(n: number): string {
  if (!Number.isFinite(n)) return "0";
  const abs = Math.abs(n);
  const decimals = abs >= 100 ? 2 : abs >= 1 ? 4 : 6;
  const rounded = Number(n.toFixed(decimals));
  // strip trailing zeros
  return String(rounded);
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
